import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

interface OfflinePaymentRequest {
  id: number;
  user_email: string;
  plan_name: string;
  amount: number;
  reference_number: string;
  bank_details: string;
  payment_instructions: string;
  admin_approval_status: 'pending' | 'approved' | 'rejected';
  admin_approval_date?: string;
  admin_approver?: string;
  admin_notes: string;
  payment_proof?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Statistics {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  totalAmount: number;
}

export const AdminOfflinePaymentsPage: React.FC = () => {
  const [offlineRequests, setOfflineRequests] = useState<OfflinePaymentRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<OfflinePaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<number | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<OfflinePaymentRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [statistics, setStatistics] = useState<Statistics>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0
  });

  // Filter and sort states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { showToast } = useToast();

  const fetchOfflineRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.adminRequest<any>('/payments/offline-payment-requests/');
      const requests: OfflinePaymentRequest[] = Array.isArray(response)
        ? response
        : (response?.results ?? []);
      setOfflineRequests(requests);

      // Calculate statistics
      const stats = {
        total: requests.length,
        pending: requests.filter(r => r.admin_approval_status === 'pending').length,
        approved: requests.filter(r => r.admin_approval_status === 'approved').length,
        rejected: requests.filter(r => r.admin_approval_status === 'rejected').length,
        totalAmount: requests.reduce((sum, r) => sum + r.amount, 0)
      };
      setStatistics(stats);
    } catch (err: any) {
      setError(err.message || 'Failed to load offline payment requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOfflineRequests();
  }, [fetchOfflineRequests]);

  // Filter and sort requests
  useEffect(() => {
    const list = Array.isArray(offlineRequests) ? offlineRequests : [];
    let filtered = [...list];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.admin_approval_status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      filtered = filtered.filter(request => {
        const requestDate = new Date(request.created_at);
        switch (dateFilter) {
          case 'today':
            return requestDate >= today;
          case 'yesterday':
            return requestDate >= yesterday && requestDate < today;
          case 'last_week':
            return requestDate >= lastWeek;
          case 'last_month':
            return requestDate >= lastMonth;
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.user_email.toLowerCase().includes(term) ||
        request.plan_name.toLowerCase().includes(term) ||
        request.reference_number.toLowerCase().includes(term) ||
        request.bank_details.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof OfflinePaymentRequest];
      let bValue: any = b[sortBy as keyof OfflinePaymentRequest];

      if (sortBy === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredRequests(filtered);
  }, [offlineRequests, statusFilter, dateFilter, searchTerm, sortBy, sortOrder]);

  const handleApprove = async (requestId: number) => {
    try {
      setProcessingAction(requestId);
      setError(null);
      await apiService.adminRequest(`/payments/offline-payment-requests/${requestId}/approve/`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes })
      });
      await fetchOfflineRequests();
      showToast('Payment request approved successfully', 'success');
      setShowDetailsModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to approve payment request');
      showToast('Failed to approve payment request', 'error');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async (requestId: number) => {
    try {
      setProcessingAction(requestId);
      setError(null);
      await apiService.adminRequest(`/payments/offline-payment-requests/${requestId}/reject/`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes })
      });
      await fetchOfflineRequests();
      showToast('Payment request rejected successfully', 'success');
      setShowDetailsModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to reject payment request');
      showToast('Failed to reject payment request', 'error');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleViewDetails = (request: OfflinePaymentRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || '');
    setShowDetailsModal(true);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getApprovalBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading offline payment requests..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Offline Payment Requests"
        subtitle="Review and manage offline payment requests from users"
        align="center"
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.total}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.pending}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Approved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.approved}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Rejected</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.rejected}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(statistics.totalAmount)}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by email, plan, reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Date Range</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_week">Last 7 Days</option>
                <option value="last_month">Last 30 Days</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Sort By</label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="created_at-desc">Newest First</option>
                <option value="created_at-asc">Oldest First</option>
                <option value="amount-desc">Amount High to Low</option>
                <option value="amount-asc">Amount Low to High</option>
                <option value="user_email-asc">Email A-Z</option>
                <option value="user_email-desc">Email Z-A</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Showing {filteredRequests.length} of {offlineRequests.length} requests
        </p>
        {filteredRequests.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setStatusFilter('all');
              setDateFilter('all');
              setSearchTerm('');
              setSortBy('created_at');
              setSortOrder('desc');
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Requests List */}
      <div className="grid gap-4">
        {filteredRequests.map(request => (
          <Card key={request.id} className="hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {request.user_email}
                    </h3>
                    <Badge variant={getApprovalBadgeVariant(request.admin_approval_status)}>
                      {request.admin_approval_status}
                    </Badge>
                    <Badge variant={getStatusBadgeVariant(request.status)}>
                      {request.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                    {request.plan_name} - {formatCurrency(request.amount)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Requested {formatRelativeDate(request.created_at)}
                  </p>
                  {request.reference_number && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Ref: {request.reference_number}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(request.amount)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bank Details</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {request.bank_details ? (
                      typeof request.bank_details === 'string' 
                        ? request.bank_details 
                        : JSON.stringify(request.bank_details)
                    ) : 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Payment Proof</p>
                  <div className="mt-1">
                    {request.payment_proof ? (
                      <a
                        href={request.payment_proof}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 inline-flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        View Proof
                      </a>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No proof uploaded</p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Instructions</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {request.payment_instructions || 'No instructions provided'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleViewDetails(request)}
                >
                  View Details
                </Button>

                {request.admin_approval_status === 'pending' && (
                  <div className="flex space-x-2">
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={processingAction === request.id}
                      onClick={() => handleReject(request.id)}
                    >
                      {processingAction === request.id ? (
                        <LoadingSpinner text="Rejecting..." />
                      ) : (
                        'Reject'
                      )}
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      disabled={processingAction === request.id}
                      onClick={() => handleApprove(request.id)}
                    >
                      {processingAction === request.id ? (
                        <LoadingSpinner text="Approving..." />
                      ) : (
                        'Approve'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}

        {filteredRequests.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No offline payment requests</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {offlineRequests.length === 0 
                  ? 'No offline payment requests to review.'
                  : 'No requests match your current filters.'
                }
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Enhanced Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80">
              <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Offline Payment Details
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Request ID: {selectedRequest.id}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 hover:text-gray-900 dark:text-white rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  onClick={() => setShowDetailsModal(false)}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Payment Information */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      User & Payment Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">User Email</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.user_email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Subscription Plan</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.plan_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Amount</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(selectedRequest.amount)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Reference Number</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.reference_number || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Status</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={getStatusBadgeVariant(selectedRequest.status)}>
                            {selectedRequest.status}
                          </Badge>
                          <Badge variant={getApprovalBadgeVariant(selectedRequest.admin_approval_status)}>
                            {selectedRequest.admin_approval_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Payment Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Bank Details</p>
                        <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-md">
                          <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                            {selectedRequest.bank_details 
                              ? (typeof selectedRequest.bank_details === 'string' 
                                  ? selectedRequest.bank_details 
                                  : JSON.stringify(selectedRequest.bank_details, null, 2))
                              : 'Not provided'
                            }
                          </pre>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Payment Instructions</p>
                        <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-md">
                          <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                            {selectedRequest.payment_instructions || 'Not provided'}
                          </p>
                        </div>
                      </div>
                      {selectedRequest.payment_proof && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Payment Proof</p>
                          <a
                            href={selectedRequest.payment_proof}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 mt-1"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            View Payment Proof
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Timestamps
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Request Created</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedRequest.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Last Updated</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedRequest.updated_at)}</p>
                    </div>
                    {selectedRequest.admin_approval_date && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Approval Date</p>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedRequest.admin_approval_date)}</p>
                      </div>
                    )}
                    {selectedRequest.admin_approver && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Approved By</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.admin_approver}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Notes */}
                {selectedRequest.admin_approval_status === 'pending' && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Admin Notes
                    </h4>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      placeholder="Add notes for approval/rejection..."
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                    Close
                  </Button>
                  {selectedRequest.admin_approval_status === 'pending' && (
                    <>
                      <Button
                        variant="danger"
                        disabled={processingAction === selectedRequest.id}
                        onClick={() => handleReject(selectedRequest.id)}
                      >
                        {processingAction === selectedRequest.id ? (
                          <LoadingSpinner text="Rejecting..." />
                        ) : (
                          'Reject Request'
                        )}
                      </Button>
                      <Button
                        variant="success"
                        disabled={processingAction === selectedRequest.id}
                        onClick={() => handleApprove(selectedRequest.id)}
                      >
                        {processingAction === selectedRequest.id ? (
                          <LoadingSpinner text="Approving..." />
                        ) : (
                          'Approve Request'
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
          </div>
        </div>
      )}
    </div>
  );
}; 